import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authAPI, apiClient, configAPI, APIError } from '../api/client';
import { serverAPI } from '../api/server';
import { useAuthStore } from '../store/authStore';
import {
  encryptWithMasterKey,
  computeFingerprint
} from '../crypto/keys';
import { signIdentityRequest, signRequestWithKey } from '../crypto/signing';
import { signDeviceKeyBinding } from '../crypto/deviceKeyBinding';
import {
  deletePendingDeviceRegistration,
  deletePendingIdentityRegistration,
  markPendingIdentityRegistrationRegistered,
  saveDevice,
  saveIdentity,
  updatePendingIdentityRegistrationActivationMeta
} from '../services/keyStatePersistence';
import {
  getOrCreatePendingCircleIdentityKeyMaterial,
  getOrCreatePendingDeviceKeyMaterial
} from '../services/pendingRegistration';
import type { RegisterIdentityPayload, RegisterDevicePayload } from '@shared/types';
import { normalizeServerUrl, serverKeyFromServerUrl } from '../utils/serverKey';
import { normalizeDeviceLabel, rememberPreferredDeviceLabel } from '../utils/deviceLabel';
import { t } from '../i18n';

type RegistrationErrorCode =
  | 'INVALID_INVITE'
  | 'INVITE_EXPIRED'
  | 'INVITE_EXHAUSTED'
  | 'REGISTRATION_FAILED'
  | 'DEVICE_REGISTRATION_FAILED'
  | 'UNKNOWN';

type BootstrapServerAdmin = {
  serverAdminId: string;
  identityId: string;
  grantedAt: string;
};

class RegistrationError extends Error {
  code: RegistrationErrorCode;

  constructor(code: RegistrationErrorCode, message: string) {
    super(message);
    this.code = code;
  }
}

export function useRegistration() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState<RegistrationErrorCode | null>(null);
  const navigate = useNavigate();
  const { addIdentity, addDevice } = useAuthStore();

  const resolveInviteMessage = (code?: string | null): string => {
    if (code === 'INVITE_EXPIRED') return t('addServer.inviteValidation.expired');
    if (code === 'INVITE_EXHAUSTED') return t('addServer.inviteValidation.exhausted');
    return t('register.errors.invalidInvite');
  };

  const register = async (
    inviteToken: string,
    options?: {
      identityName?: string;
      deviceLabel?: string;
      publishIdentity?: boolean;
      noNamesOnServer?: boolean;
      navigateOnSuccess?: boolean;
      serverUrl?: string;
      ownerClaimToken?: string;
    }
  ): Promise<
    | {
        ok: true;
        identityId: string;
        serverUrl: string;
        serverId: string;
        ownerClaimApplied: boolean;
        serverAdmin: BootstrapServerAdmin | null;
      }
    | { ok: false; code: RegistrationErrorCode; message: string }
  > => {
    setIsLoading(true);
    setError(null);
    setErrorCode(null);

    try {
      const targetServerUrl = normalizeServerUrl(options?.serverUrl || apiClient.getBaseUrl());
      if (!targetServerUrl) {
        throw new RegistrationError('UNKNOWN', t('common.circleConnectionFailed'));
      }
      apiClient.setBaseUrl(targetServerUrl);

      // 1. Check invite validity
      let inviteCheck;
      try {
        inviteCheck = await authAPI.checkInvite(inviteToken, targetServerUrl);
      } catch (e) {
        // Some deployments/proxies may return non-JSON errors (e.g. HTML) for invalid tokens.
        // Treat common "invalid" HTTP statuses as an invalid invite.
        if (e instanceof APIError && [400, 404, 405].includes(e.status)) {
          throw new RegistrationError('INVALID_INVITE', t('register.errors.invalidInvite'));
        }
        throw e;
      }
      if (inviteCheck.status === 'error') {
        const code = inviteCheck.error?.code;
        if (code === 'INVITE_EXPIRED') {
          throw new RegistrationError('INVITE_EXPIRED', resolveInviteMessage(code));
        }
        if (code === 'INVITE_EXHAUSTED') {
          throw new RegistrationError('INVITE_EXHAUSTED', resolveInviteMessage(code));
        }
        if (code === 'INVITE_INVALID') {
          throw new RegistrationError('INVALID_INVITE', resolveInviteMessage(code));
        }
        throw new RegistrationError(
          'INVALID_INVITE',
          inviteCheck.error?.message || resolveInviteMessage(code)
        );
      }

      const serverNameFromInvite = inviteCheck.result?.serverName || '';
      let noNamesOnServer = inviteCheck.result?.noNamesOnServer ?? options?.noNamesOnServer ?? false;

      // 2. Reuse one identity key for every attempt to register with this circle.
      const pendingIdentity = await getOrCreatePendingCircleIdentityKeyMaterial(targetServerUrl);
      const identityPublicKey = pendingIdentity.record.publicKey;
      const generatedIdentityId = pendingIdentity.record.identityId;
      const identityPrivateKeyBytes = pendingIdentity.privateKeyBytes;
      const pendingFingerprint = await computeFingerprint(identityPublicKey);
      const pendingServerId = serverKeyFromServerUrl(targetServerUrl) || '';
      await updatePendingIdentityRegistrationActivationMeta(generatedIdentityId, {
        serverId: pendingServerId,
        serverName: serverNameFromInvite,
        serverUrl: targetServerUrl,
        identityName: options?.identityName || '',
        noNamesOnServer,
        fingerprint: pendingFingerprint
      });

      // 3. Encrypt identity private key with master key (if available)
      const masterKey = useAuthStore.getState().masterKey;
      let encryptedIdentityPrivateKey: import('@shared/types').EncryptedBlob | undefined;
      if (masterKey) {
        const identityPrivateKeyBase64 = btoa(String.fromCharCode(...identityPrivateKeyBytes));
        encryptedIdentityPrivateKey = await encryptWithMasterKey(masterKey, identityPrivateKeyBase64);
      }

      // 5. Register identity
      const shouldPublish = !noNamesOnServer && !!options?.publishIdentity;
      const registerPayload: RegisterIdentityPayload = {
        inviteToken,
        identityPublicKey,
        encryptedIdentityPrivateKey,
        publishIdentity: shouldPublish,
        identityName: shouldPublish ? options?.identityName : undefined
      };

      const registerResponse = options?.ownerClaimToken
        ? await authAPI.registerOwner({
            ...registerPayload,
            ownerClaimToken: options.ownerClaimToken
          }, targetServerUrl)
        : await authAPI.register(registerPayload, targetServerUrl);
      if (registerResponse.status === 'error') {
        const code = registerResponse.error?.code;
        if (code === 'INVITE_EXPIRED') {
          throw new RegistrationError('INVITE_EXPIRED', resolveInviteMessage(code));
        }
        if (code === 'INVITE_EXHAUSTED') {
          throw new RegistrationError('INVITE_EXHAUSTED', resolveInviteMessage(code));
        }
        if (code === 'INVITE_INVALID') {
          throw new RegistrationError('INVALID_INVITE', resolveInviteMessage(code));
        }
        throw new RegistrationError(
          'REGISTRATION_FAILED',
          registerResponse.error?.message || t('register.errors.registrationFailed')
        );
      }

      const { identity } = registerResponse.result!;
      if (identity.identityId !== generatedIdentityId) {
        throw new RegistrationError('REGISTRATION_FAILED', 'Server returned an unexpected identity ID');
      }
      const serverAdmin = options?.ownerClaimToken
        ? (registerResponse.result as { serverAdmin?: BootstrapServerAdmin | null } | undefined)?.serverAdmin ?? null
        : null;
      const fingerprint = pendingFingerprint;

      let serverId = '';
      let serverName = '';
      const serverUrl = targetServerUrl;
      serverId = serverKeyFromServerUrl(serverUrl) || '';
      serverName = serverNameFromInvite;
      await markPendingIdentityRegistrationRegistered(generatedIdentityId, {
        identity,
        activationMeta: {
          serverId,
          serverName,
          serverUrl,
          identityName: options?.identityName || '',
          publishIdentity: shouldPublish,
          presenceVisible: true,
          noNamesOnServer,
          fingerprint,
          isServerAdmin: !!serverAdmin,
          isPrimaryVpsTarget: !!serverAdmin
        }
      });

      // 6. Reuse one device key for this pending identity across retries.
      const normalizedDeviceLabel = normalizeDeviceLabel(options?.deviceLabel);
      const pendingDevice = await getOrCreatePendingDeviceKeyMaterial(
        identity.identityId,
        serverUrl,
        normalizedDeviceLabel || undefined
      );
      const pendingDeviceId = pendingDevice.pendingId;
      const devicePublicKey = pendingDevice.publicKey;
      const deviceEncryptionPublicKey = pendingDevice.encryptionPublicKey;
      const devicePrivateKeyBytes = pendingDevice.privateKeyBytes;
      const deviceEncryptionPrivateKeyBytes = pendingDevice.encryptionPrivateKeyBytes;
      const deviceLabel = pendingDevice.label || normalizedDeviceLabel || undefined;

      // 7. Register device
      const devicePayload: RegisterDevicePayload = {
        deviceId: pendingDeviceId,
        devicePublicKey,
        deviceEncryptionPublicKey,
        deviceKeyBinding: await signDeviceKeyBinding({
          identityId: identity.identityId,
          devicePublicKey,
          deviceEncryptionPublicKey,
          devicePrivateKey: devicePrivateKeyBytes
        }),
        deviceLabel
      };

      const signedDeviceRequest = await signIdentityRequest(
        'auth:register-device',
        devicePayload,
        identity.identityId,
        identityPrivateKeyBytes
      );
      const deviceResponse = await authAPI.registerDevice(signedDeviceRequest, serverUrl);
      if (deviceResponse.status === 'error') {
        throw new RegistrationError(
          'DEVICE_REGISTRATION_FAILED',
          deviceResponse.error?.message || t('register.errors.deviceRegistrationFailed')
        );
      }

      const device = deviceResponse.result!;
      if (device.deviceId !== pendingDeviceId) {
        throw new RegistrationError('DEVICE_REGISTRATION_FAILED', 'Server returned an unexpected device ID');
      }

      // 8. Save everything locally (device first so its signingKey is available for server name request)
      const deviceCryptoKeys = await saveDevice(
        device.deviceId,
        device.identityId,
        device.publicKey,
        devicePrivateKeyBytes,
        device.label || deviceLabel,
        device.encryptionPublicKey || deviceEncryptionPublicKey,
        deviceEncryptionPrivateKeyBytes
      );

      // Fetch canonical server name only after device registration (device-signed)
      try {
        const signed = await signRequestWithKey(
          'config:server-name',
          {},
          device.deviceId,
          deviceCryptoKeys.signingKey
        );
        const nameResp = await configAPI.getServerName(signed, serverUrl);
        if (nameResp.status === 'ok' && nameResp.result?.serverName) {
          serverName = nameResp.result.serverName;
        }
        if (nameResp.status === 'ok' && typeof nameResp.result?.noNamesOnServer === 'boolean') {
          noNamesOnServer = nameResp.result.noNamesOnServer;
        }
      } catch {
        // ignore (fallback to invite-provided name or serverId)
      }

      let vpsId: string | undefined;
      try {
        const signed = await signRequestWithKey('server:vps-id', {}, device.deviceId, deviceCryptoKeys.signingKey);
        const vpsResp = await serverAPI.getVpsId(signed, serverUrl);
        if (vpsResp.status === 'ok' && vpsResp.result?.vpsId) {
          vpsId = vpsResp.result.vpsId;
        }
      } catch {
        // ignore — vpsId will be missing until next sync
      }

      const identityCryptoKeys = await saveIdentity(
        identity.identityId,
        identity.publicKey,
        identityPrivateKeyBytes,
        {
          serverId,
          serverName,
          serverUrl,
          role: identity.role,
          vpsId,
          fingerprint,
          identityName: options?.identityName || '',
          publishIdentity: shouldPublish,
          presenceVisible: true,
          noNamesOnServer
        }
      );
      await Promise.all([
        deletePendingIdentityRegistration(generatedIdentityId),
        deletePendingDeviceRegistration(pendingDeviceId)
      ]);

      // 9. Update store
      addIdentity({
        identityId: identity.identityId,
        serverId,
        serverName: serverName || serverId,
        serverUrl,
        publicKey: identity.publicKey,
        fingerprint,
        role: identity.role,
        vpsId,
        ...identityCryptoKeys,
        identityName: options?.identityName || '',
        publishIdentity: shouldPublish,
        presenceVisible: true,
        noNamesOnServer
      });
      addDevice({
        deviceId: device.deviceId,
        identityId: device.identityId,
        publicKey: device.publicKey,
        encryptionPublicKey: device.encryptionPublicKey || deviceEncryptionPublicKey,
        label: device.label || deviceLabel,
        ...deviceCryptoKeys
      });
      rememberPreferredDeviceLabel(device.label || deviceLabel);

      const _mk = useAuthStore.getState().masterKey;
      if (_mk) {
        import('../utils/circleBackup').then(({ uploadCircleBackup }) => {
          const s = useAuthStore.getState();
          uploadCircleBackup({ ...s, masterKey: _mk }).catch(() => {});
        }).catch(() => {});
      }

      // After joining a circle, land on the circles list instead of chats.
      if (options?.navigateOnSuccess !== false) {
        navigate('/servers');
      }
      return {
        ok: true,
        identityId: identity.identityId,
        serverUrl: serverUrl || '',
        serverId,
        ownerClaimApplied: !!options?.ownerClaimToken,
        serverAdmin
      };
    } catch (err) {
      if (err instanceof RegistrationError) {
        setErrorCode(err.code);
        setError(err.message);
        return { ok: false, code: err.code, message: err.message };
      } else {
        const message = err instanceof Error ? err.message : 'Registration failed';
        setErrorCode('UNKNOWN');
        setError(message);
        return { ok: false, code: 'UNKNOWN', message };
      }
    } finally {
      setIsLoading(false);
    }
  };

  return { register, isLoading, error, errorCode };
}
